import { observer } from 'mobx-react-lite'
import { ReactElement, useState } from 'react'
import styled from 'styled-components'
import InterchangeDisclaimer from '../interchange/InterchangeDisclaimer'
import { ProductModel } from 'interfaces'
import ResultsTable from './ResultsTable'
import { useResultsStore } from '../../../store/ResultsStore'
import { Collapse, LinearProgress } from '@mui/material'
import InterchangeInfoMessage from '../interchange/InterchangeInfoMessage'
import { defaultTheme } from '../../../../../../src/theme/index'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { UserAttributeKey } from '../../../../../../src/store/user/interfaces'
import { useUserStore } from '../../../../../../src/store/user/UserStore'
import { breakpoints } from '../../../../../../src/theme/breakpoints'

interface Props {
  groupName: string
  parts: ProductModel[]
}

const Group = observer((props: Props): ReactElement => {
  const { groupName, parts } = props
  const userStore = useUserStore()
  const resultsStore = useResultsStore()
  const { showInterchangeDisclaimers, lastInterchangeTextSearched } =
    resultsStore
  const [expanded, setExpanded] = useState(true)

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const showHeader = showInterchangeDisclaimers
  const crossReference: boolean =
    userStore.getUserAttribute(UserAttributeKey.crossReference) === 'true'
  const showFooter = showHeader && crossReference

  const ArrowIcon = expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />

  const manufacturer = parts?.[0]?.heavyDutyGroup?.manufacturer
  const partType = parts?.[0]?.heavyDutyGroup?.partType
  return (
    <Wrapper>
      <GroupContent>
        {showHeader && (
          <GroupHeader>
            <IconWrapper onClick={handleExpandClick}>{ArrowIcon}</IconWrapper>
            <InterchangeDisclaimer
              partType={partType}
              manufacturer={manufacturer}
              partNumber={lastInterchangeTextSearched}
            />
          </GroupHeader>
        )}

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <ResultsTable key={`results_for_${groupName.trim()}`} parts={parts} />
          {showFooter && (
            <InterchangeInfoMessage partNumber={lastInterchangeTextSearched} />
          )}
        </Collapse>
      </GroupContent>

      {resultsStore.isLoading && <LinearProgress />}
    </Wrapper>
  )
})

const Wrapper = styled.div``

const GroupContent = styled.div`
  @media screen and (min-width: ${breakpoints.tablet}px) and (max-width: ${breakpoints.laptopMedium -
    1}px) {
    width: max-content;
    min-width: 100%;
  }
`

const GroupHeader = styled.div`
  display: flex;
  min-height: 57px;
  height: auto;
  padding: 10px;
  gap: 12px;
  align-items: center;
  background: ${defaultTheme.colors.lightBlue};
  width: 100%;
  box-sizing: border-box;

  @media screen and (width <= ${breakpoints.mobileLarge}px) {
    align-items: flex-start;
    padding: 12px 10px;
  }
`

const IconWrapper = styled.span`
  cursor: pointer;
  flex-shrink: 0;

  @media screen and (width <= ${breakpoints.mobileLarge}px) {
    margin-top: 2px;
  }

  & svg {
    font-size: 2rem;
  }
`

export default Group
